import { ChevronDown, Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { CompanyAutocomplete } from "~/components/search/company-autocomplete";
import { useAuth } from "~/lib/hooks/use-auth";

const navLinks = [
	{ label: "Ceník", href: "/cenik" },
	{ label: "O nás", href: "/o-nas" },
];

const serviceLinks = [
	{
		label: "Účetní a daňové kanceláře",
		href: "/pro-ucetni-a-danove-poradce",
	},
	{
		label: "Finanční a nákupní týmy",
		href: "/pro-financni-tymy",
	},
	{
		label: "Právní a insolvenční praxe",
		href: "/pro-pravniky-a-insolvencni-spravce",
	},
];

export function shouldShowNavigationSearch(pathname: string) {
	return pathname !== "/search";
}

export function Navbar() {
	const { user, loading } = useAuth();
	const location = useLocation();
	const [mobileOpen, setMobileOpen] = useState(false);
	const previousPathname = useRef(location.pathname);

	useEffect(() => {
		if (previousPathname.current === location.pathname) return;
		previousPathname.current = location.pathname;
		setMobileOpen(false);
	}, [location.pathname]);

	return (
		<header className="sticky top-0 z-50 px-3 pt-3 sm:px-4">
			<nav className="glass-panel-strong mx-auto flex max-w-7xl items-center justify-between rounded-2xl px-5 py-3.5 lg:px-7 lg:py-2">
				<Link to="/" className="shrink-0">
					<img src="/logo.png" alt="FirmIndex" className="h-10" />
				</Link>

				{/* Desktop nav */}
				<div className="hidden items-center gap-4 lg:flex xl:gap-6">
					{shouldShowNavigationSearch(location.pathname) && (
						<CompanyAutocomplete
							className="w-60 xl:w-72 2xl:w-80"
							formLabel="Vyhledávání firem v hlavní navigaci"
							placeholder="Hledat firmu…"
							submitIconOnly
						/>
					)}
					<div className="group relative">
						<button
							type="button"
							className="flex items-center gap-1 text-sm font-medium text-muted transition-colors hover:text-text"
						>
							Služby
							<ChevronDown className="h-3.5 w-3.5" />
						</button>
						<div className="glass-panel-strong invisible absolute left-0 top-full mt-2 w-64 rounded-2xl py-2 opacity-0 transition-all group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100">
							{serviceLinks.map((link) => (
								<Link
									key={link.href}
									to={link.href}
									className="block px-4 py-2.5 text-sm text-muted transition-colors hover:bg-gray-50 hover:text-text"
								>
									{link.label}
								</Link>
							))}
						</div>
					</div>
					{navLinks.map((link) => (
						<Link
							key={link.href}
							to={link.href}
							className="text-sm font-medium text-muted hover:text-text transition-colors"
						>
							{link.label}
						</Link>
					))}
					{!loading &&
						(user ? (
							<Link
								to="/dashboard"
								className="rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-600 transition-colors"
							>
								Dashboard
							</Link>
						) : (
							<Link
								to="/login?mode=register"
								className="rounded-full bg-accent-500 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-accent-600 transition-colors"
							>
								Vyzkoušet zdarma
							</Link>
						))}
				</div>

				{/* Mobile toggle */}
				<button
					type="button"
					className="p-2 text-muted hover:text-text lg:hidden"
					onClick={() => setMobileOpen(!mobileOpen)}
					aria-label={mobileOpen ? "Zavřít menu" : "Otevřít menu"}
				>
					{mobileOpen ? <X size={24} /> : <Menu size={24} />}
				</button>
			</nav>

			{/* Mobile menu */}
			{mobileOpen && (
				<nav
					aria-label="Mobilní navigace"
					className="glass-panel-strong mx-auto mt-2 max-w-7xl rounded-2xl px-6 pb-6 lg:hidden"
				>
					{shouldShowNavigationSearch(location.pathname) && (
						<div className="pt-3">
							<CompanyAutocomplete
								formLabel="Vyhledávání firem v mobilním menu"
								placeholder="Hledat firmu…"
							/>
						</div>
					)}
					<div className="pt-2">
						<p className="py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
							Služby
						</p>
						{serviceLinks.map((link) => (
							<Link
								key={link.href}
								to={link.href}
								className="block py-2 text-sm font-medium text-muted hover:text-text"
								onClick={() => setMobileOpen(false)}
							>
								{link.label}
							</Link>
						))}
					</div>
					{navLinks.map((link) => (
						<Link
							key={link.href}
							to={link.href}
							className="block py-3 text-sm font-medium text-muted hover:text-text"
							onClick={() => setMobileOpen(false)}
						>
							{link.label}
						</Link>
					))}
					{!loading &&
						(user ? (
							<Link
								to="/dashboard"
								className="mt-2 block rounded-full bg-primary-500 px-5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-primary-600 transition-colors"
								onClick={() => setMobileOpen(false)}
							>
								Dashboard
							</Link>
						) : (
							<Link
								to="/login?mode=register"
								className="mt-2 block rounded-full bg-accent-500 px-5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-accent-600 transition-colors"
								onClick={() => setMobileOpen(false)}
							>
								Vyzkoušet zdarma
							</Link>
						))}
				</nav>
			)}
		</header>
	);
}
